/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, OnChanges, OnDestroy, SimpleChanges, inject, signal} from '@angular/core';
import {resourceOptionParams} from '../common/controller/signal-admin-resource-api';
import {BufferedSignalValue, bufferTemplateSignalValue, createBufferedSignalValue, deferSignalStateWrite} from '../common/util/template-state';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './order-information-page.component.html';

import {lineItemCommons} from '../line-item/line-item-commons';
import {advertiserNavigationParams} from '../../components/util/navigation-context';
import {AdnLogService} from '../../components/util/adn-log.service';
import {AppLocaleService} from '../common/locale/app-locale.service';

import {MarketplaceProductApiService} from '../admin/marketplace-product/marketplace-product-api.service';
import {NetworkDataApiService} from '../admin/network/network-data-api.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {StateService} from '@uirouter/core';
import {SignalAdminResourceListState, SignalAdminResourceListStateFactory} from '../common/controller/signal-admin-resource-list-state';
import {CreativeApiService, CreativeModel} from '../creative/creative-api.service';
import {LineItemApiService, LineItemModel} from '../line-item/line-item-api.service';


type OrderInformationTab = 'lineItems' | 'charts' | 'creatives';

function emptyCountList(): {totalCountText: string} {
  // Seed list count state before the list helper is attached.
  return {totalCountText: ''};
}

@Component({
  standalone: false,
  selector: 'adn-order-information-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderInformationPageComponent implements OnChanges, OnDestroy {
  @Input() model: any;
  private _isNewDirect: boolean | undefined;
  private _readOnlyDirect: boolean | undefined;

  @Input()
  set isNew(value: boolean) {
    this._isNewDirect = value;
  }

  get isNew(): boolean {
    return this._isNewDirect;
  }

  @Input()
  set readOnly(value: boolean) {
    this._readOnlyDirect = value;
  }

  get readOnly(): boolean {
    return this._readOnlyDirect;
  }

  effectiveIsNew = false;
  activeTab: OrderInformationTab = 'lineItems';
  chartsLoaded = false;

  isAxAdvertiser = false;
  chartCurrency: any;
  showCreatives = false;

  readonly lineItems = signal<SignalAdminResourceListState<LineItemModel> | null>(null);
  readonly creatives = signal<SignalAdminResourceListState<CreativeModel> | null>(null);
  readonly lineItemsEmpty = signal(true);
  readonly lineItemsCount = signal(0);
  readonly selLineItem = signal<any>(null);
  /** Snapshot — list-helper replaces lineItems.items mid-CD (NG0100). */
  readonly displayLineItems = signal<any[]>([]);

  showLineItemForm = false;
  lineItem: any;
  readonly lineItemView = signal<any>(null, {equal: () => false});
  objectiveFields: any = {};
  bidSpecificationFields: any = {};
  isSelfService = false;

  private listInitTimer?: ReturnType<typeof setTimeout>;
  private lineItemCommonsApi: any;
  private destroyed = false;
  private readonly lineItemListState = createBufferedSignalValue<SignalAdminResourceListState<LineItemModel> | null>(null);
  private readonly creativeListState = createBufferedSignalValue<SignalAdminResourceListState<CreativeModel> | null>(null);
  private readonly lineItemCountState = createBufferedSignalValue(0);
  private readonly creativeCountState = createBufferedSignalValue(0);
  private readonly signalListFactory = inject(SignalAdminResourceListStateFactory);
  private readonly creativeApi = inject(CreativeApiService);
  private readonly lineItemApi = inject(LineItemApiService);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(NetworkDataApiService) private readonly NetworkData: NetworkDataApiService,
    @Inject(MarketplaceProductApiService) private readonly MarketplaceProduct: MarketplaceProductApiService,
    private readonly adnLog: AdnLogService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly translate: TranslateService,
    private readonly appLocale: AppLocaleService,
    private readonly $state: StateService,
    @Inject(LineItemApiService) private readonly LineItem: LineItemApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    this.isAxAdvertiser = LocalUserPermissions.isAxAdvertiser();
    this.showCreatives = !!LocalUserPermissions.getSectionPerms().creatives;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.model) {
      this.chartCurrency = _.get(this.model, ['objectives', 'BUDGET', 'currency']);
    }
    if (changes.model || changes.isNew) {
      this.scheduleListInit();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.listInitTimer) {
      clearTimeout(this.listInitTimer);
    }
  }

  selectTab(tab: OrderInformationTab): void {
    this.activeTab = tab;
    if (tab === 'charts') {
      this.chartsLoaded = true;
    }
  }

  get displayLineItemCount(): number {
    return this.bufferListCount(this.lineItemCountState, this.lineItems());
  }

  get displayLineItemList(): SignalAdminResourceListState<LineItemModel> | null {
    return this.bufferList(this.lineItemListState, this.lineItems());
  }

  get displayCreativeCount(): number {
    return this.bufferListCount(this.creativeCountState, this.creatives());
  }

  get displayCreativeList(): SignalAdminResourceListState<CreativeModel> | null {
    return this.bufferList(this.creativeListState, this.creatives());
  }

  loadExternalLineItem(): void {
    const viewType = this.LocalNetworkProfile.isCalt() && this.LocalUserPermissions.canUseCaltLineItemViewType2() ? 2 : 1;
    this.$state.go('app.adv.line-items.new', {
      viewType,
      full: viewType === 2,
      orderId: this.model.id,
      orderName: this.model.name,
      advertiserId: _.get(this.model, ['advertiser', 'id']),
      advertiserName: _.get(this.model, ['advertiser', 'name'])
    });
  }

  loadLineItemForm(): void {
    const networkDefaults = this.LocalNetworkProfile.getDefaults();
    const networkNow = this.adnTimeShifter.getMomentInNetworkTimezone();
    let startDate = networkNow.clone().startOf('day').toDate();
    let endDate = networkNow.clone().add(networkDefaults.lineItemDaysApart, 'day').endOf('day').toDate();
    if (_.isDate(this.model.objectiveStartDate)) {
      startDate = this.model.objectiveStartDate;
    }
    if (_.isDate(this.model.objectiveEndDate)) {
      endDate = this.model.objectiveEndDate;
    }

    this.lineItem = {
      type: 'AUCTION',
      companionAds: 'NONE',
      deliveryCurve: 'EVEN',
      bidSpecification: {cpm: {}, cpc: {}},
      targeting: {},
      userState: this.isSelfService || this.isAxAdvertiser ? networkDefaults.marketplaceLineItemUserState || 'PROPOSED' : networkDefaults.lineItemUserState || 'APPROVED',
      order: this.model,
      startDate,
      endDate,
      modStartDate: startDate,
      modEndDate: endDate
    };

    this.lineItem.deliveryCurve = this.isAxAdvertiser
      ? this.LocalNetworkProfile.getDefaults().marketplaceDeliveryCurve
      : this.LocalNetworkProfile.getDefaults().deliveryCurve;

    if (this.isAxAdvertiser) {
      this.loadAxProductDefaults();
    }

    this.lineItemCommonsApi = lineItemCommons(
      this,
      this.translate,
      this.appLocale,
      this.LocalNetworkProfile,
      this.LocalUserPermissions,
      this.adnTimeShifter,
      this.localNetworkInfo,
      'lineItem'
    );
    this.lineItemCommonsApi.setBidSpecificationFields();
    this.lineItemCommonsApi.setObjectivesAsString();
    this.lineItemCommonsApi.setObjectiveFields();
    this.objectiveFields = this.lineItemCommonsApi.objectiveFields || {};
    this.bidSpecificationFields = this.lineItemCommonsApi.bidSpecificationFields || {};

    const defaultTierId = this.LocalNetworkProfile.get('defaultTierId');
    if (defaultTierId) {
      this.lineItem.tier = {id: defaultTierId};
    }

    this.lineItemView.set(this.lineItem);
    this.showLineItemForm = true;
  }

  lineItemObjectivesAsString(): string {
    return this.lineItemCommonsApi?.objectivesAsString?.() || '';
  }

  onBeforeLineItemSubmit(): void {
    this.lineItem.startDate = _.isDate(this.lineItem.modStartDate) ? this.lineItem.modStartDate : null;
    this.lineItem.endDate = _.isDate(this.lineItem.modEndDate) ? this.lineItem.modEndDate : null;
  }

  onLineItemSaved(lineItem: any): void {
    this.showLineItemForm = false;
    void lineItem;
    this.lineItems()?.load();
  }

  onLineItemFormCancelled(): void {
    this.showLineItemForm = false;
  }

  newBulkCreative(): void {
    this.$state.go('app.adv.creatives.bulkupload', advertiserNavigationParams(this.model.advertiser));
  }

  newCreative(): void {
    this.$state.go('app.adv.creatives.new', advertiserNavigationParams(this.model.advertiser));
  }

  private initLists(): void {
    if (!this.model?.id || this.effectiveIsNew) {
      this.lineItems.set(null);
      this.creatives.set(null);
      this.lineItemsEmpty.set(true);
      this.lineItemsCount.set(0);
      this.displayLineItems.set([]);
      this.selLineItem.set(null);
      return;
    }

    const lineItems = this.signalListFactory.create(this.lineItemApi, {
      listId: 'fullLineItemsForOrder', baseTable: 'lineItem', syncPageToUrl: false,
      additionalParams: {order: this.model.id},
      transformResults: (items) => {
        deferSignalStateWrite(() => this.snapshotLineItems(items));
        return items;
      }
    }, this.destroyRef);
    lineItems.readOnly.set(!!this.readOnly);
    this.lineItems.set(lineItems);
    lineItems.load();

    const creatives = this.signalListFactory.create(this.creativeApi, {
      listId: 'fullCreativesForOrder', baseTable: 'creative', syncPageToUrl: false,
      additionalParams: {order: this.model.id}
    }, this.destroyRef);
    creatives.readOnly.set(!!this.readOnly);
    this.creatives.set(creatives);
    creatives.load();
  }

  private loadAxProductDefaults(): void {
    let teamToProduct: any;
    const defaultsQuery = firstValueFrom(this.NetworkData.listOptions(resourceOptionParams({filterByValue: 'defaultMarketplaceProducts', filterBy: 'type'}))).then((page: any) => {
      if (_.get(page, ['results', 'length'])) {
        const entry = page.results[0];
        try {
          const mapping = entry.data.teamToProduct;
          teamToProduct = _.isString(mapping) ? JSON.parse(mapping) : mapping;
        } catch {
          this.adnLog.warn('Found a problem find teams to products for defaults');
        }
      }
    });

    Promise.resolve(defaultsQuery).then(() => {
      firstValueFrom(this.MarketplaceProduct.listOptions(resourceOptionParams({excludeInvalid: true}))).then((page: any) => {
        const marketplaceProducts = page.results;
        if (teamToProduct) {
          const matchedMpId = _.find(teamToProduct, (val: any, key: string) => {
            const isTeam = this.LocalUserPermissions.has('AX_ADVERTISER', key);
            return isTeam && _.find(marketplaceProducts, (mp: any) => mp.id === teamToProduct[key]);
          });
          const matchedMp = _.find(marketplaceProducts, ['id', matchedMpId]);
          if (matchedMp) {
            this.lineItem.axProduct = matchedMp;
            _.set(this.lineItem, 'targeting', matchedMp.targeting);
            this.lineItem.rateLimits = matchedMp.rateLimits;
          }
        }
        this.lineItemView.set(this.lineItem);
      });
    });
  }

  selectLineItem(lineItem: any): void {
    this.selLineItem.set(lineItem);
  }

  private snapshotLineItems(items: any[] = this.lineItems()?.items() || []): void {
    const displayLineItems = items.slice();
    this.displayLineItems.set(displayLineItems);
    this.lineItemsEmpty.set(displayLineItems.length === 0);
    this.lineItemsCount.set(displayLineItems.length);
    this.selLineItem.set(displayLineItems[0]);
  }

  private scheduleListInit(): void {
    if (this.listInitTimer) {
      clearTimeout(this.listInitTimer);
    }
    // Keep list population out of the input check that creates the tab badges.
    this.listInitTimer = setTimeout(() => {
      if (!this.destroyed) {
        this.initLists();
      }
    }, 0);
  }

  private bufferListCount(state: BufferedSignalValue<number>, list: {totalCount(): number} | null): number {
    return bufferTemplateSignalValue(state, list?.totalCount() || 0, () => !this.destroyed);
  }

  private bufferList<T>(state: BufferedSignalValue<T | null>, list: T | null): T | null {
    return bufferTemplateSignalValue(state, list, () => !this.destroyed);
  }

}
